import { useEffect, useRef } from 'react';
import { getAudioContext } from '@/lib/audio/audio-context';

const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_SECONDS = 0.1;
const CLICK_FREQUENCY_HZ = 1000;
const CLICK_DURATION_SECONDS = 0.05;
const CLICK_GAIN = 0.3;

type UseMetronomeParams = {
  bpm: number;
  multiplier: number;
  on: boolean;
};

function scheduleClick(ctx: AudioContext, startTime: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = CLICK_FREQUENCY_HZ;
  gain.gain.setValueAtTime(CLICK_GAIN, startTime);
  gain.gain.linearRampToValueAtTime(0, startTime + CLICK_DURATION_SECONDS);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startTime);
  osc.stop(startTime + CLICK_DURATION_SECONDS);
}

export function useMetronome({ bpm, multiplier, on }: UseMetronomeParams): void {
  const nextClickTimeRef = useRef<number>(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!on) {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const ctx = getAudioContext();
    const effectiveBpm = bpm * multiplier;
    const beatInterval = 60 / effectiveBpm;
    nextClickTimeRef.current = ctx.currentTime;

    function tick(): void {
      const scheduleUntil = ctx.currentTime + SCHEDULE_AHEAD_SECONDS;
      while (nextClickTimeRef.current < scheduleUntil) {
        scheduleClick(ctx, nextClickTimeRef.current);
        nextClickTimeRef.current += beatInterval;
      }
    }

    tick();
    intervalRef.current = setInterval(tick, LOOKAHEAD_MS);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [bpm, multiplier, on]);
}
